import dayjs from 'dayjs';
import pluralize from 'pluralize';

import { DATE_SIMPLE } from '@cast/constants';
import {
  Divider,
  Icons,
  Skeleton,
  Tooltip,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';
import { CspIcon } from '@cast/shared-csp-icons-ui';
import { Cloud } from '@cast/shared-types';
import { BillingSubscriptionInfo } from '@cast/types';

const providerDisplayName: Record<string, string> = {
  gcp: 'GCP',
  aws: 'AWS',
  azure: 'Azure',
};

const SUBSCRIBE_PROVIDERS: Array<{
  provider: Cloud;
  href: string;
}> = [
  { provider: Cloud.gcp, href: 'https://cast.ai/pricing/' },
  { provider: Cloud.azure, href: 'https://cast.ai/pricing/' },
  { provider: Cloud.aws, href: 'https://cast.ai/pricing/' },
];

type SubscriptionItemProps = {
  subscription: BillingSubscriptionInfo;
};

const SubscriptionItem = ({ subscription }: SubscriptionItemProps) => {
  const { provider } = subscription;
  const displayName = provider
    ? providerDisplayName[provider] ?? provider
    : '–';
  const daysRemaining = subscription.endDate
    ? dayjs(subscription.endDate).diff(dayjs(), 'day')
    : undefined;

  const daysSection = daysRemaining !== undefined && (
    <Tooltip
      title={
        subscription.startDate || subscription.endDate ? (
          <Stack gap={2} alignItems="flex-end">
            {subscription.startDate && (
              <Typography
                color={tokens.theme.fg.structural.primaryOnInverse}
                variant="P12R"
              >
                Signed: {dayjs(subscription.startDate).format(DATE_SIMPLE)}
              </Typography>
            )}
            {subscription.endDate && (
              <Typography
                color={tokens.theme.fg.structural.primaryOnInverse}
                variant="P12R"
              >
                Active until: {dayjs(subscription.endDate).format(DATE_SIMPLE)}
              </Typography>
            )}
          </Stack>
        ) : undefined
      }
    >
      <Stack
        direction="row"
        gap={4}
        alignItems="center"
        justifyContent="center"
      >
        <Icons.Clock size={16} />
        <Typography variant="P12R" color={tokens.theme.fg.structural.secondary}>
          {daysRemaining} {pluralize('days', daysRemaining)}
        </Typography>
      </Stack>
    </Tooltip>
  );

  return (
    <Stack direction="row" gap={16} alignItems="center">
      <Stack direction="row" gap={4} alignItems="center">
        {provider && provider in Cloud && (
          <CspIcon csp={Cloud[provider as keyof typeof Cloud]} size={24} />
        )}
        <Typography color={tokens.theme.fg.structural.primary} variant="P12R">
          {displayName}
        </Typography>
      </Stack>
      {daysSection}
    </Stack>
  );
};

type SubscriptionsBarProps = {
  subscriptions: BillingSubscriptionInfo[];
  isLoading?: boolean;
};

export const SubscriptionsBar = ({
  subscriptions,
  isLoading,
}: SubscriptionsBarProps) => {
  const isEmpty = !isLoading && subscriptions.length === 0;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        backgroundColor: tokens.theme.bg.structural.surface,
        borderRadius: '4px',
        boxShadow: tokens.theme.shadows.elevation.medium,
        px: 16,
        height: 42,
      }}
    >
      {isLoading ? (
        <Skeleton variant="rectangular" width={400} height={16} />
      ) : isEmpty ? (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flex={1}
        >
          <Typography
            color={tokens.theme.fg.structural.secondary}
            variant="L10R"
            sx={{ textTransform: 'uppercase' }}
          >
            No marketplace subscription
          </Typography>
          <Stack direction="row" gap={8} alignItems="center">
            <Typography
              variant="L10R"
              color={tokens.theme.fg.structural.secondary}
            >
              Subscribe:
            </Typography>
            <Stack direction="row" gap={6} alignItems="center">
              {SUBSCRIBE_PROVIDERS.map(({ provider, href }) => (
                <Box
                  key={provider}
                  component="a"
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <CspIcon csp={provider} size={24} />
                </Box>
              ))}
            </Stack>
          </Stack>
        </Stack>
      ) : (
        <Stack direction="row" gap={8} alignItems="center" flex={1}>
          <Box sx={{ pr: 32 }}>
            <Typography
              variant="L10R"
              color={tokens.theme.fg.structural.secondary}
              sx={{ textTransform: 'uppercase' }}
            >
              Subscription active on:
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center">
            {subscriptions.map((subscription, index) => (
              <Stack
                key={subscription.provider ?? index}
                direction="row"
                alignItems="center"
              >
                {index > 0 && (
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ mx: 16, my: 4 }}
                  />
                )}
                <SubscriptionItem subscription={subscription} />
              </Stack>
            ))}
          </Stack>
        </Stack>
      )}
    </Box>
  );
};
